import React from 'react';
import {
  Breadcrumb, Button, Card, Drawer, Empty, Modal, Space, Tooltip, Typography, notification,
} from 'antd';
import Answers from '../Answers/Answers';
import AttachForm from './AttachForm';
import { getChains } from '../api/answers';
import { deleteQuestion, detachQuestion, getTransitionId } from '../api/transitions';
import { showServerError } from '../api/errors';

const { Text } = Typography;

// Путь хранится парами id, а не объектами: дерево перезагружается после каждой
// правки, и объекты из прошлой загрузки моментально устаревают.
const resolvePath = (rootQuestion, path) => {
  const nodes = [];
  let question = rootQuestion;

  for (let i = 0; i < path.length; i++) {
    if (i > 0) {
      question = getChains(nodes[i - 1].answer).find((q) => q.id === path[i].questionId);
    }
    if (!question || question.id !== path[i].questionId) return nodes;

    const answer = (question.answers || []).find((a) => a.id === path[i].answerId);
    if (!answer) return nodes;

    nodes.push({ question, answer });
  }

  return nodes;
};

function AdditionalQuestionsDrawer(props) {
  const {
    survey, path, setPath, onClose, refresh,
  } = props;

  const rootQuestion = (survey?.connectedQuestions || [])
    .find((q) => q.id === path[0]?.questionId);
  const nodes = resolvePath(rootQuestion, path);
  const current = nodes[nodes.length - 1];

  // Плоский список: antd Breadcrumb ожидает Breadcrumb.Item прямыми детьми.
  const crumbs = [{ key: 'survey', text: survey?.surveyname }];
  nodes.forEach((node) => {
    crumbs.push({ key: `q-${node.question.id}`, text: node.question.questiontext });
    crumbs.push({ key: `a-${node.answer.id}`, text: `ответ «${node.answer.answertxt}»` });
  });

  const openChain = (question, answer) => {
    setPath([...path.slice(0, nodes.length), { questionId: question.id, answerId: answer.id }]);
  };

  const goBack = () => {
    if (nodes.length <= 1) onClose();
    else setPath(path.slice(0, nodes.length - 1));
  };

  const detach = async (additionalQuestion) => {
    try {
      await detachQuestion(getTransitionId(additionalQuestion));
      notification.success({ message: 'Вопрос отвязан' });
      await refresh();
    } catch (e) {
      showServerError(e, 'Не удалось отвязать вопрос');
    }
  };

  const removeQuestion = (additionalQuestion) => {
    Modal.confirm({
      title: 'Удалить вопрос целиком?',
      content: (
        <>
          <p>
            Вопрос «
            {additionalQuestion.questiontext}
            » будет удалён вместе со своими ответами.
          </p>
          <Text type="danger">
            Он исчезнет из всех цепочек, где используется, а не только из этой.
          </Text>
        </>
      ),
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteQuestion(additionalQuestion.id);
          notification.success({ message: 'Вопрос удалён' });
          await refresh();
        } catch (e) {
          showServerError(e, 'Не удалось удалить вопрос');
        }
      },
    });
  };

  const renderChain = (additionalQuestion) => {
    const transitionId = getTransitionId(additionalQuestion);

    return (
      <Card
        key={additionalQuestion.id}
        title={additionalQuestion.questiontext}
        style={{ marginBottom: '18px' }}
        extra={(
          <Space>
            <Tooltip
              title={transitionId
                ? ''
                : 'Сервер не возвращает id связи в дереве анкеты — отвязать можно только связь, созданную в этом сеансе. Ждём доработки API'}
            >
              <Button disabled={!transitionId} onClick={() => detach(additionalQuestion)}>
                Отвязать
              </Button>
            </Tooltip>
            <Button danger onClick={() => removeQuestion(additionalQuestion)}>
              Удалить вопрос
            </Button>
          </Space>
        )}
      >
        <Answers
          data={additionalQuestion}
          getData={refresh}
          onOpenChain={openChain}
        />
      </Card>
    );
  };

  return (
    <Drawer
      title="Дополнительные вопросы"
      placement="right"
      width={960}
      visible={path.length > 0}
      onClose={onClose}
      destroyOnClose
      extra={<Button onClick={goBack}>Назад</Button>}
    >
      {current ? (
        <>
          <Breadcrumb style={{ marginBottom: '18px' }}>
            {crumbs.map((crumb) => (
              <Breadcrumb.Item key={crumb.key}>{crumb.text}</Breadcrumb.Item>
            ))}
          </Breadcrumb>

          {getChains(current.answer).length > 0
            ? getChains(current.answer).map(renderChain)
            : (
              <Empty
                description={`На ответ «${current.answer.answertxt}» пока не привязано ни одного вопроса`}
              />
            )}

          <AttachForm
            answer={current.answer}
            ownerQuestionId={current.question.id}
            onAttached={refresh}
          />
        </>
      ) : (
        <Empty description="Ответ больше не существует — возможно, он был пересоздан или удалён" />
      )}
    </Drawer>
  );
}

export default AdditionalQuestionsDrawer;
